import { KeyRound, Loader2, ShieldCheck, Smartphone } from "lucide-react";

import { useMfaRequired } from "../../../components/security/useMfaRequired";
import { isPasskeySupported } from "../../../utils/webauthn";
import { Button } from "@/v2/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/v2/ui/dialog";
import { EnrollmentDialog } from "./EnrollmentDialog";
import { EnrollmentOption } from "./EnrollmentPrompt";
import { ReauthenticateDialog } from "./ReauthenticateDialog";

/** A project requires two-step verification: add a factor or confirm with one, then carry on here */
export function MfaRequiredDialog() {
    const flow = useMfaRequired();
    const { overview } = flow;
    const passkeySupported = isPasskeySupported();
    const who = flow.projectName || "This project";

    let body;
    if (flow.loading) {
        body = <div className="grid place-items-center py-4 text-text-3"><Loader2 className="size-5 animate-spin" /></div>;
    } else if (flow.mustSignIn) {
        body = <p className="text-sm text-text-2">{flow.federated ? "Sign in again with Google or GitHub. Your provider's two-step verification counts here." : "Sign in again with two-step verification to continue."}</p>;
    } else if (flow.hasFactors) {
        body = <p className="text-sm text-text-2">Confirm it's you with your passkey or authenticator app. You'll stay on this page.</p>;
    } else {
        body = (
            <div className="flex flex-col gap-2">
                <p className="mb-1 text-sm text-text-2">Add one now, then confirm with it. It takes about a minute.</p>
                {overview?.passkeysAvailable && (
                    <EnrollmentOption
                        icon={<KeyRound />}
                        title="Passkey"
                        badge="Recommended"
                        text={passkeySupported ? "Face ID, Touch ID, your screen lock or a security key." : "This browser can't create passkeys."}
                        disabled={!passkeySupported}
                        onClick={() => flow.enroll("passkey")}
                    />
                )}
                {overview?.totpAvailable && (
                    <EnrollmentOption
                        icon={<Smartphone />}
                        title="Authenticator app"
                        text="A six-digit code from 1Password, Google Authenticator or Authy."
                        onClick={() => flow.enroll("totp")}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            <Dialog open={flow.visible && flow.step === "required"} onOpenChange={next => !next && flow.close()}>
                <DialogContent data-v2="" className="sm:max-w-md">
                    <DialogHeader>
                        <span className="mb-2 grid size-10 place-items-center rounded-xl bg-soft text-text-2 [&_svg]:size-5">
                            <ShieldCheck />
                        </span>
                        <DialogTitle className="font-display text-lg font-semibold">Two-step verification required</DialogTitle>
                        <DialogDescription className="leading-relaxed">{who} asks every member to use two-step verification.</DialogDescription>
                    </DialogHeader>
                    {body}
                    <DialogFooter>
                        <Button variant="outline" onClick={flow.close}>Not now</Button>
                        {flow.mustSignIn && <Button onClick={flow.signIn}>Sign in again</Button>}
                        {!flow.loading && !flow.mustSignIn && flow.hasFactors && <Button onClick={flow.confirm}>Continue</Button>}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <EnrollmentDialog
                open={flow.visible && flow.step === "enroll"}
                initialMethod={flow.method}
                onComplete={flow.enrollmentComplete}
                onClose={flow.enrollmentClosed}
            />
            <ReauthenticateDialog
                open={flow.visible && flow.step === "confirm"}
                description={`Confirm it's you with two-step verification to open ${flow.projectName || "the project"}.`}
                onSuccess={flow.confirmed}
                onCancel={flow.close}
            />
        </>
    );
}
